
import { useState, useCallback } from "react";
import styles from "./findprogram.module.css";
import programsData from "../findprogram.json";
import ProgramNotAvailable from "../components/Findprogramdata/programnotavailble";
import { Link, useNavigate } from "react-router-dom";
import DigitalHealthFAB from "../components/Digitalhealth";

// ─────────────────────────────────────────────────────────────
// DATA — loaded from programs.json
// ─────────────────────────────────────────────────────────────
const DATA = programsData;

// ─────────────────────────────────────────────────────────────
// SHARED COMPONENTS
// ─────────────────────────────────────────────────────────────


const Badge = ({ variant = "purple", children }) => {
  const variantMap = {
    purple: styles.fpBadgePurple,
    pink: styles.fpBadgePink,
    green: styles.fpBadgeGreen,
    orange: styles.fpBadgeOrange,
    blue: styles.fpBadgeBlue,
    gray: styles.fpBadgeGray,
  };
  return <span className={`${styles.fpBadge} ${variantMap[variant] || styles.fpBadgePurple}`}>{children}</span>;
};

const Btn = ({ variant = "primary", full, lg, className = "", onClick, children, disabled }) => {
  const variantMap = {
    primary: styles.fpBtnPrimary,
    secondary: styles.fpBtnSecondary,
    ghost: styles.fpBtnGhost,
  };
  return (
    <button
      className={[
        styles.fpBtn,
        variantMap[variant] || styles.fpBtnPrimary,
        full ? styles.fpBtnFull : "",
        lg ? styles.fpBtnLg : "",
        className,
      ].join(" ")}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

const Stepper = ({ steps, current }) => (
  <div className={styles.fpStepper}>
    {steps.map((s, i) => {
      const done = i < current, active = i === current;
      return (
        <div key={i} className={styles.fpStepItem}>
          <div className={[
            styles.fpStepDot,
            done ? styles.fpStepDotDone : active ? styles.fpStepDotActive : styles.fpStepDotPending
          ].join(" ")}>
            {done ? "✓" : i + 1}
          </div>
          <span className={[
            styles.fpStepLabel,
            done ? styles.fpStepLabelDone : active ? styles.fpStepLabelActive : styles.fpStepLabelPending
          ].join(" ")}>{s}</span>
          {i < steps.length - 1 && (
            <div className={[styles.fpStepLine, i < current ? styles.fpStepLineDone : styles.fpStepLinePending].join(" ")} />
          )}
        </div>
      );
    })}
  </div>
);

const TrustBar = () => (
  <div className={styles.fpTrustBar}>
    {[
      { icon: "🛡️", title: "Clinically designed", desc: "Programs built by experts with evidence-based care." },
      { icon: "🔒", title: "Private & secure", desc: "Your data is protected with industry-leading security." },
      { icon: "👩‍⚕️", title: "Expert care teams", desc: "Access gynecologists, nurses, and care coordinators." },
      { icon: "💜", title: "Personalized pathways", desc: "Care plans tailored to your needs and life stage." },
    ].map((t, i) => (
      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <span style={{ fontSize: 20 }}>{t.icon}</span>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2 }}>{t.title}</div>
          <div style={{ fontSize: 11, color: "var(--ava-color-caption)", lineHeight: 1.4 }}>{t.desc}</div>
        </div>
      </div>
    ))}
  </div>
);

const SummaryCard = ({ data }) => (
  <div className={styles.fpCard}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
      <div style={{ fontSize: 15, fontWeight: 700 }}>Your Answers</div>
      {/* <span style={{ fontSize: 12, color: "var(--ava-color-primary-700)", fontWeight: 600, cursor: "pointer" }}>✏ Edit</span> */}
    </div>
    {[
      { icon: "👤", label: "Life Stage", val: data.lifeStage || "—" },
      { icon: "🎯", label: "Concern", val: data.concern || "—" },
      { icon: "🛡️", label: "Safety Status", val: data.safetyStatus || "—", green: data.safetyStatus === "Routine" },
      { icon: "⏱️", label: "Duration", val: data.duration || "—" },
      { icon: "☑️", label: "Next Step", val: data.nextStep || "—" },
    ].map((r, i) => (
      <div key={i} className={styles.fpSumRow}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>{r.icon}</span>
          <div>
            <div style={{ fontSize: 11, color: "var(--ava-color-caption)", marginBottom: 2 }}>{r.label}</div>
            <div className={`${styles.fpSumVal}${r.green ? ` ${styles.fpSumValGreen}` : ""}`}>{r.val}</div>
          </div>
        </div>
      </div>
    ))}
    <div className={styles.fpInfoBox} style={{ marginTop: 12 }}>
      ✦ We'll use your answers to suggest the best programs for you.
    </div>
  </div>
);

const CareNavigatorCard = () => {
  const navigate = useNavigate(); // ✅ Move hook to the top of the component body

  return (
    <div className={styles.fpCard} style={{ marginTop: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Need help?</div>
      <div style={{ fontSize: 11, color: "var(--ava-color-caption)", marginBottom: 12, lineHeight: 1.4 }}>
        Our care navigator can help you understand your options.
      </div>
      <Btn variant="ghost" full onClick={() => navigate("/care-navigator")}>
        💬 Ask Care Navigator
      </Btn>
    </div>
  );
};


// const BottomNav = ({ screen, setScreen }) => {
//   const items = [
//     ["🏠", "landing", "Home"],
//     ["📋", "listing", "Programs"],
//     ["🧭", "choose",  "Choose"],
//     ["✅", "eligibility", "Check"],
//     ["🎉", "enrollment",  "Enrolled"],
//   ];
//   return (
//     <div className={styles.fpBottomNav}>
//       {items.map(([emoji, key, label]) => (
//         <button key={key} className={styles.fpBottomBtn} onClick={() => setScreen(key)}>
//           <span style={{ fontSize: "clamp(16px,4vw,20px)" }}>{emoji}</span>
//           <span style={{
//             fontSize: 9,
//             color: screen === key ? "var(--ava-color-primary-700)" : "var(--ava-color-caption)",
//             fontWeight: screen === key ? 700 : 400,
//             textAlign: "center",
//           }}>{label}</span>
//         </button>
//       ))}
//     </div>
//   );
// };

// Dynamic question renderer — questions come from per-program JSON data
const QSection = ({ q, answers, onAnswer }) => {
  const val = answers[q.id];
  const isMulti = q.type === "multi";
  const toggle = (v) => {
    if (!isMulti) { onAnswer(q.id, v); return; }
    const prev = val || [];
    onAnswer(q.id, prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  };
  const isSelected = (v) => isMulti ? (val || []).includes(v) : val === v;
  const isDone = isMulti ? (val || []).length > 0 : !!val;

  return (
    <div className={styles.fpQSection}>
      <div className={styles.fpQHeader}>
        <div className={`${styles.fpQNum}${isDone ? ` ${styles.fpQNumDone}` : ""}`}>
          {isDone ? "✓" : q.num}
        </div>
        <span className={styles.fpQLabel}>
          {q.label}
          {q.subLabel && <span className={styles.fpQSub}> {q.subLabel}</span>}
        </span>
      </div>
      <div className={styles.fpChipGrid}>
        {q.options.map(o => {
          const sel = isSelected(o.value);
          return (
            <button
              key={o.value}
              className={`${styles.fpChip}${sel ? ` ${styles.fpChipSel}` : ""}`}
              onClick={() => toggle(o.value)}
              style={{ position: "relative" }}
            >
              {sel && isMulti && <span className={styles.fpCheckDot}>✓</span>}
              {o.icon && <span style={{ fontSize: 16 }}>{o.icon}</span>}
              <span>{o.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SCREEN: LANDING
// ─────────────────────────────────────────────────────────────
const LandingScreen = ({ setScreen, setSelectedLifeStage }) => (
  <div className={`${styles.fpContainer} ${styles.fpScreen}`}>
    {/* Hero */}
    <div className={styles.fpHeroGrid}>
      <div>
        <h1 style={{ fontSize: "35px", fontWeight: 900, lineHeight: 1.1, marginBottom: 8 }}>
          Find the Right Program<br />
          <span style={{ color: "var(--ava-color-secondary-700)" }}>for You</span>
        </h1>
        <p style={{ fontSize: 14, color: "var(--ava-color-body)", lineHeight: 1.6, maxWidth: 360, marginBottom: 28 }}>
          Answer a few simple questions and we'll recommend care programs based on your life stage, symptoms, and health goals.
        </p>
        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
          <Btn onClick={() => { setSelectedLifeStage(null); setScreen("listing"); }}>Find My Program</Btn>
          <Btn variant="secondary" onClick={() => setScreen("choose")}> Help Me Choose</Btn>
        </div>
        {/* <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--ava-color-caption)", flexWrap: "wrap" }}>
          <span>🔒 100% Secure</span><span>•</span><span>🔐 Private</span><span>•</span><span>👩‍⚕️ Clinically Guided</span>
        </div> */}
      </div>

      {/* Hero illustration — hidden on mobile */}
      <div className={`${styles.fpHeroCenter}`} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className={styles.fpHeroCircle}>
          <span><img src="/images/Landingprogram.svg" className="w-96 h-96" /></span>
          {/* {[
            { icon: "📅", style: { top: 16,  left: 16  } },
            { icon: "🛡️", style: { top: 16,  right: 16 } },
            { icon: "💗", style: { bottom: 24, left: 8  } },
          ].map(({ icon, style }, i) => (
            <div key={i} className={styles.fpHeroFloatIcon} style={style}>{icon}</div>
          ))} */}
        </div>
      </div>

      {/* Why card */}
      <div className={styles.fpCard}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Why choose a care program?</div>
        {[
          { icon: "👥", title: "Personalized care", desc: "Programs designed for your life stage and health needs." },
          { icon: "🩺", title: "Expert guidance", desc: "Access gynecologists, nurses and care coordinators." },
          { icon: "📋", title: "Structured journey", desc: "Step-by-step care plan with follow-ups." },
          { icon: "📊", title: "Better outcomes", desc: "Track progress and improve your long-term health." },
        ].map((w, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: i < 3 ? 14 : 0 }}>
            <div className={styles.fpWhyItem}>{w.icon}</div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2 }}>{w.title}</div>
              <div style={{ fontSize: 11, color: "var(--ava-color-caption)", lineHeight: 1.4 }}>{w.desc}</div>
            </div>
          </div>
        ))}
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--ava-color-primary-50)" }}>
          <span
            style={{ fontSize: 12, color: "var(--ava-color-primary-700)", fontWeight: 600, cursor: "pointer" }}
            onClick={() => setScreen("listing")}
          >
            Learn more about our programs →
          </span>
        </div>
      </div>
    </div>

    {/* ── Explore by Life Stage ── */}
    <div style={{ marginTop: 40 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <h2 style={{ fontSize: "clamp(16px,2.5vw,20px)", fontWeight: 700 }}>Explore programs by life stage</h2>
        <span
          style={{ fontSize: 13, color: "var(--ava-color-primary-700)", fontWeight: 600, cursor: "pointer" }}
          onClick={() => { setSelectedLifeStage(null); setScreen("listing"); }}
        >
          View all programs →
        </span>
      </div>
      <div className={styles.fpLsGrid}>
        {DATA.lifeStages.map((ls) => (
          <div
            key={ls.id}
            className={styles.fpLifeStageCard}
            onClick={() => { setSelectedLifeStage(ls.id); setScreen("listing"); }}
          >
            <div className={styles.fpLifeStageEmoji}>{ls.emoji}</div>
            <div className={styles.fpLifeStageLabel}>
              {ls.label}
              {ls.ageRange && (
                <span style={{ fontWeight: 400, color: "var(--ava-color-caption)" }}> ({ls.ageRange})</span>
              )}
            </div>
            <div className={styles.fpLifeStageDesc}>{ls.desc}</div>
            <div className={styles.fpLifeStageLink}>View programs →</div>
          </div>
        ))}
      </div>
    </div>

    <TrustBar />
  </div>
);

// ─────────────────────────────────────────────────────────────
// SCREEN: LISTING  (filtered by life stage if set)
// ─────────────────────────────────────────────────────────────
const ListingScreen = ({ setScreen, setSelectedProgram, selectedLifeStage, setSelectedLifeStage }) => {
  const [search, setSearch] = useState("");
  const [filterLS, setFilterLS] = useState(selectedLifeStage ? [selectedLifeStage] : []);
  const [filterType, setFilterType] = useState([]);

  const toggleLS = (v) => setFilterLS(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  const toggleType = (v) => setFilterType(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  const clearAll = () => { setFilterLS([]); setFilterType([]); };

  const activeStage = filterLS.length === 1 ? DATA.lifeStages.find(ls => ls.id === filterLS[0]) : null;

  const filtered = DATA.programs.filter(p => {
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.short.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterLS.length > 0 && !filterLS.some(ls => p.lifeStageIds.includes(ls))) return false;
    if (filterType.length > 0 && !filterType.includes(p.type)) return false;
    return true;
  });

  const FilterGroup = ({ title, opts, arr, toggle }) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>{title}</div>
      {opts.map(o => (
        <label key={o.value} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer" }}>
          <div
            className={`${styles.fpFilterCheckbox}${arr.includes(o.value) ? ` ${styles.fpFilterCheckboxActive}` : ""}`}
            onClick={() => toggle(o.value)}
          >
            {arr.includes(o.value) && <span style={{ color: "#fff", fontSize: 10 }}>✓</span>}
          </div>
          <span style={{
            fontSize: 12,
            color: arr.includes(o.value) ? "var(--ava-color-primary-700)" : "var(--ava-color-caption)",
            fontWeight: arr.includes(o.value) ? 600 : 400,
          }}>{o.label}</span>
        </label>
      ))}
    </div>
  );

  return (
    <div className={`${styles.fpContainer} ${styles.fpScreen}`}>
      <div style={{ marginBottom: 24 }}>
        {activeStage ? (
          <div>
            <div className={styles.fpListingLifeStageHeader}>
              <div className={styles.fpListingLifeStageEmoji}>{activeStage.emoji}</div>
              <div>
                <h1 style={{ fontSize: "22px", fontWeight: 900 }}>{activeStage.label} Programs</h1>
                <p style={{ fontSize: 13, color: "var(--ava-color-caption)" }}>{activeStage.desc}</p>
              </div>
            </div>
            <span
              style={{ fontSize: 13, color: "var(--ava-color-primary-700)", fontWeight: 600, cursor: "pointer" }}
              onClick={() => { setFilterLS([]); setSelectedLifeStage(null); }}
            >
              ← View all programs
            </span>
          </div>
        ) : (
          <>
            <div className="scj-header">
              <div>
                <h1 style={{ fontSize: "25px", fontWeight: 900, marginBottom: 4 }}>Explore Women's Health Programs</h1>
                <p style={{ fontSize: 14, color: "var(--ava-color-body)" }}>
                  Browse personalised care programs by life stage, concern, and support needs.
                </p>
              </div>
              <Btn style={{ fontSize: 14, color: "var(--ava-color-body)" }} onClick={() => setScreen("landing")}
              // to={onSkip} 
              >
                Back
              </Btn>
            </div>
          </>
        )}
      </div>

      <div className={styles.fpFilterGrid}>
        {/* Sidebar filters */}
        <div className={`${styles.fpCard} ${styles.fpCardSticky}`}>
          <FilterGroup
            title="Life Stage"
            opts={DATA.lifeStages.map(ls => ({ value: ls.id, label: ls.label + (ls.ageRange ? ` (${ls.ageRange})` : "") }))}
            arr={filterLS} toggle={toggleLS}
          />
          {/* <FilterGroup
            title="Program Type"
            opts={["Assessment-led", "Doctor-led", "High-touch care", "Wellness program"].map(v => ({ value: v, label: v }))}
            arr={filterType} toggle={toggleType}
          /> */}
          <Btn variant="ghost" full onClick={clearAll}>🔄 Clear Filters</Btn>
        </div>

        {/* Program cards */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 16, flexWrap: "wrap" }}>
            <div style={{ fontSize: 14, color: "var(--ava-color-body)" }}>
              <strong style={{ color: "var(--ava-color-heading)" }}>{filtered.length}</strong> programs found
            </div>
            <div className="flex gap-2">
            <div className={styles.fpSearchWrap}>
              <span className={styles.fpSearchIcon}>🔍</span>
              <input
                className={styles.fpSearchInput}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search programs, symptoms..."
              />
            </div>
            <button className="btn-primary">Talk to Care Guide</button>
</div>
          </div>

          {filtered.length === 0 ? (
            <div className={styles.fpCard} style={{ textAlign: "center", padding: "40px 20px" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>No programs found</div>
              <div style={{ fontSize: 13, color: "var(--ava-color-caption)", marginBottom: 16 }}>
                Try adjusting your filters or search terms
              </div>
              <Btn onClick={clearAll}>Clear all filters</Btn>
            </div>
          ) : (
            <div className={styles.fpProgGrid}>
              {filtered.map(p => (
                <div key={p.id} className={styles.fpProgCard}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
                    <div style={{ width: 52, height: 52, borderRadius: 14, background: "linear-gradient(135deg, var(--ava-color-secondary-50), var(--ava-color-primary-100))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>
                      {p.emoji}
                    </div>
                    <Badge variant={p.status === "doctor_review" ? "orange" : "green"}>
                      {p.status === "eligible" ? "✅ Available" : "ℹ Doctor Review"}
                    </Badge>
                  </div>
                  {p.popular && <div style={{ marginBottom: 8 }}><Badge variant="pink">⭐ Popular</Badge></div>}
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, lineHeight: 1.3 }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: "var(--ava-color-caption)", lineHeight: 1.5, marginBottom: 12 }}>{p.short}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 12 }}>
                    {p.tags.slice(0, 1).map(t => <Badge key={t} variant="purple">{t}</Badge>)}
                    <Badge variant="gray">{p.type}</Badge>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--ava-color-caption)", marginBottom: 14 }}>
                    ⏱ {p.duration} · 🖥 {p.mode}
                  </div>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    <Btn onClick={() => {
                      setSelectedProgram(p);
                      setScreen(p.status === "doctor_review" ? "doctor" : "eligibility");
                    }}>Check Eligibility</Btn>
                    {/* <Btn onClick={() => { setSelectedProgram(p); setScreen("eligibility"); }}>Check Eligibility</Btn> */}
                    <Btn variant="secondary" onClick={() => { setSelectedProgram(p); setScreen("detail"); }}>Learn More</Btn>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <TrustBar />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SCREEN: HELP ME CHOOSE
// ─────────────────────────────────────────────────────────────
// const HelpMeChooseScreen = ({ setScreen }) => {
//   const [lifeStage, setLifeStage] = useState("");
//   const [concerns,  setConcerns]  = useState([]);
//   const [safety,    setSafety]    = useState([]);
//   const [duration,  setDuration]  = useState("");
//   const [nextStep,  setNextStep]  = useState("");

//   const toggleConcern = v => setConcerns(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);

//   const answerData = {
//     lifeStage,
//     concern:      concerns.join(", "),
//     safetyStatus: safety.includes("None of the above") ? "Routine" : safety.length > 0 ? "Urgent" : "—",
//     duration,
//     nextStep,
//   };

//   const lsOpts = DATA.lifeStages.map(ls => ({ label: ls.label, emoji: ls.emoji }));
//   const concernsList  = ["Irregular Periods", "PCOS Concern", "Trying to Conceive", "Pregnancy Check-up", "Hot Flashes / Menopause", "Preventive Wellness"];
//   const concernIcons  = { "Irregular Periods": "📅", "PCOS Concern": "🌸", "Trying to Conceive": "👶", "Pregnancy Check-up": "🤰", "Hot Flashes / Menopause": "🌡", "Preventive Wellness": "🛡️" };

//   const QSec = ({ num, done, label, subLabel, children }) => (
//     <div className={styles.fpQSection}>
//       <div className={styles.fpQHeader}>
//         <div className={`${styles.fpQNum}${done ? ` ${styles.fpQNumDone}` : ""}`}>{done ? "✓" : num}</div>
//         <span className={styles.fpQLabel}>{label}{subLabel && <span className={styles.fpQSub}> — {subLabel}</span>}</span>
//       </div>
//       {children}
//     </div>
//   );

//   return (
//     <div className={`${styles.fpContainer} ${styles.fpScreen}`}>
//       <h1 style={{ fontSize: "clamp(18px,3vw,28px)", fontWeight: 900, marginBottom: 6 }}>Let's find the right program for you</h1>
//       <p style={{ fontSize: 13, color: "var(--ava-color-body)", marginBottom: 24, maxWidth: 500 }}>
//         Answer a few simple questions and we'll recommend the most relevant women's health program.
//       </p>
//       <div className={styles.fpTwoCol}>
//         <div className={styles.fpCard}>
//           <Stepper steps={["Life Stage", "Concern", "Symptoms & Safety", "Next Step"]} current={2} />

//           <QSec num={1} done={!!lifeStage} label="Which life stage best describes you?">
//             <div className={styles.fpChipGrid}>
//               {lsOpts.map(o => (
//                 <button
//                   key={o.label}
//                   className={`${styles.fpChip}${lifeStage === o.label ? ` ${styles.fpChipSel}` : ""}`}
//                   onClick={() => setLifeStage(o.label)}
//                 >
//                   <span style={{ fontSize: 18 }}>{o.emoji}</span>
//                   <span style={{ fontSize: "clamp(11px,1.3vw,12px)", lineHeight: 1.2 }}>{o.label}</span>
//                 </button>
//               ))}
//             </div>
//           </QSec>

//           <QSec num={2} done={concerns.length > 0} label="What would you like help with today?" subLabel="Select all that apply">
//             <div className={styles.fpChipGrid}>
//               {concernsList.map(c => {
//                 const sel = concerns.includes(c);
//                 return (
//                   <button
//                     key={c}
//                     className={`${styles.fpChip}${sel ? ` ${styles.fpChipSel}` : ""}`}
//                     onClick={() => toggleConcern(c)}
//                     style={{ position: "relative" }}
//                   >
//                     {sel && <span className={styles.fpCheckDot}>✓</span>}
//                     <span style={{ fontSize: 16 }}>{concernIcons[c]}</span>
//                     <span style={{ fontSize: "clamp(11px,1.3vw,12px)" }}>{c}</span>
//                   </button>
//                 );
//               })}
//             </div>
//           </QSec>

//           <QSec num={3} done={safety.length > 0} label="Are you experiencing any of these today?">
//             <div className={styles.fpChipGrid}>
//               {["⚡ Severe Pain", "🩸 Heavy Bleeding", "💫 Dizziness", "🌡 Fever", "✅ None of These"].map(s => {
//                 const parts  = s.split(" "), icon = parts[0], label = parts.slice(1).join(" ");
//                 const isNone = label === "None of These", val = isNone ? "None of the above" : label;
//                 const sel    = safety.includes(val);
//                 return (
//                   <button
//                     key={s}
//                     className={`${styles.fpChip}${sel ? (isNone ? ` ${styles.fpChipSelGreen}` : ` ${styles.fpChipSelRed}`) : ""}`}
//                     onClick={() => setSafety(isNone ? ["None of the above"] : [val])}
//                   >
//                     <span style={{ fontSize: 16 }}>{icon}</span>
//                     <span style={{ fontSize: "clamp(10px,1.3vw,12px)" }}>{label}</span>
//                   </button>
//                 );
//               })}
//             </div>
//             <p style={{ fontSize: 11, color: "var(--ava-color-caption)", marginTop: 8 }}>
//               If you're experiencing severe symptoms, please contact a healthcare professional.
//             </p>
//           </QSec>

//           <QSec num={4} done={!!duration} label="How long has this been affecting you?">
//             <div className={styles.fpChipGrid}>
//               {["A few days", "1–4 weeks", "1–3 months", "3+ months"].map(d => (
//                 <button
//                   key={d}
//                   className={`${styles.fpChip}${duration === d ? ` ${styles.fpChipSel}` : ""}`}
//                   onClick={() => setDuration(d)}
//                 >
//                   <span style={{ fontSize: "clamp(11px,1.3vw,12px)" }}>{d}</span>
//                 </button>
//               ))}
//             </div>
//           </QSec>

//           <QSec num={5} done={!!nextStep} label="What would you prefer next?">
//             <div className={styles.fpChipGrid}>
//               {[{ label: "Book Doctor", icon: "📅" }, { label: "Take Assessment", icon: "📋" }, { label: "Explore Program", icon: "🔍" }, { label: "Request Callback", icon: "📞" }].map(n => (
//                 <button
//                   key={n.label}
//                   className={`${styles.fpChip}${nextStep === n.label ? ` ${styles.fpChipSel}` : ""}`}
//                   onClick={() => setNextStep(n.label)}
//                 >
//                   <span style={{ fontSize: 20 }}>{n.icon}</span>
//                   <span style={{ fontSize: 12 }}>{n.label}</span>
//                 </button>
//               ))}
//             </div>
//           </QSec>

//           <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 16, borderTop: "1px solid var(--ava-color-primary-50)", flexWrap: "wrap", gap: 8 }}>
//             <Btn variant="ghost" onClick={() => setScreen("listing")}>← Back</Btn>
//             <Btn onClick={() => setScreen("listing")}>See My Program Options →</Btn>
//           </div>
//         </div>

//         <div>
//           <SummaryCard data={answerData} />
//           <CareNavigatorCard />
//         </div>
//       </div>
//       <TrustBar />
//     </div>
//   );
// };

// ─────────────────────────────────────────────────────────────
// SCREEN: HELP ME CHOOSE  (multi-step, one question per step)
// ─────────────────────────────────────────────────────────────
const HelpMeChooseScreen = ({ setScreen, setSelectedLifeStage }) => {
  const [step, setStep] = useState(0);
  const [lifeStageId, setLifeStageId] = useState("");
  const [lifeStage, setLifeStage] = useState("");
  const [concern, setConcern] = useState("");   // single concern value
  const [symptoms, setSymptoms] = useState([]);   // multi symptoms
  const [safety, setSafety] = useState([]);
  const [duration, setDuration] = useState("");
  const [nextStep, setNextStep] = useState("");
  const [ageGroup, setAgeGroup] = useState("");

  // ── Derived: current life stage object from JSON
  const currentLS = DATA.lifeStages.find(ls => ls.id === lifeStageId) || null;

  // ── Derived: concerns list for selected life stage
  const concernsList = currentLS ? currentLS.concerns : [];

  // ── Derived: symptoms list for selected concern
  const selectedConcernObj = concernsList.find(c => c.value === concern) || null;
  const symptomsList = selectedConcernObj ? selectedConcernObj.symptoms : [];

  const handleLifeStageSelect = (id) => {
    if (id === lifeStageId) return;
    setLifeStageId(id);
    const ls = DATA.lifeStages.find(o => o.id === id);
    setLifeStage(ls ? ls.label : "");
    // Reset downstream
    setConcern("");
    setSymptoms([]);
    setSafety([]);
    setDuration("");
    setAgeGroup("");
  };

  const handleConcernSelect = (val) => {
    if (val === concern) return;
    setConcern(val);
    setSymptoms([]); // reset symptoms when concern changes
  };

  const toggleSymptom = v =>
    setSymptoms(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);

  const STEPS = ["Life Stage", "Concern", "Symptoms & Safety", "Next Step"];

  const stepValid = [
    !!lifeStage,
    !!concern,
    safety.length > 0 && !!duration,
    !!nextStep,
  ];

  const answerData = {
    lifeStage,
    concern: selectedConcernObj ? selectedConcernObj.label : "—",
    safetyStatus: safety.includes("None of the above") ? "Routine" : safety.length > 0 ? "Urgent" : "—",
    duration,
    nextStep,
  };

  const handleSeePrograms = () => {
    const ls = DATA.lifeStages.find(o => o.id === lifeStageId);
    setSelectedLifeStage(ls ? ls.id : null);
    setScreen("listing");
  };

  return (
    <div className={`${styles.fpContainer} ${styles.fpScreen}`}>
      <h1 style={{ fontSize: "clamp(18px,3vw,28px)", fontWeight: 900, marginBottom: 6 }}>
        Let's find the right program for you
      </h1>
      <p style={{ fontSize: 13, color: "var(--ava-color-body)", marginBottom: 24, maxWidth: 500 }}>
        Answer a few simple questions and we'll recommend the most relevant women's health program.
      </p>

      <div className={styles.fpTwoCol}>
        <div className={styles.fpCard}>
          <Stepper steps={STEPS} current={step} />

          {/* ── STEP 0: Life Stage ── */}
          {step === 0 && (
            <div className={styles.fpQSection}>
              <div className={styles.fpQHeader}>
                <div className={`${styles.fpQNum}${lifeStage ? ` ${styles.fpQNumDone}` : ""}`}>
                  {lifeStage ? "✓" : 1}
                </div>
                <span className={styles.fpQLabel}>Which life stage best describes you?</span>
              </div>
              <div className={styles.fpChipGrid}>
                {DATA.lifeStages.map(ls => (
                  <button
                    key={ls.id}
                    className={`${styles.fpChip}${lifeStageId === ls.id ? ` ${styles.fpChipSel}` : ""}`}
                    onClick={() => handleLifeStageSelect(ls.id)}
                  >
                    <span style={{ fontSize: 18 }}>{ls.emoji}</span>
                    <span style={{
                      fontSize: "clamp(11px,1.3vw,12px)", lineHeight: 1.2,
                      textAlign: "center", display: "flex", flexDirection: "column",
                      alignItems: "center", gap: 2
                    }}>
                      {ls.label}
                      {ls.ageRange && (
                        <span style={{
                          fontWeight: 400,
                          color: lifeStageId === ls.id ? "inherit" : "var(--ava-color-caption)",
                          fontSize: "clamp(10px,1.1vw,11px)"
                        }}>({ls.ageRange})</span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── STEP 1: Concern (dynamic per life stage) ── */}
          {step === 1 && (
            <div className={styles.fpQSection}>
              <div className={styles.fpQHeader}>
                <div className={`${styles.fpQNum}${concern ? ` ${styles.fpQNumDone}` : ""}`}>
                  {concern ? "✓" : 2}
                </div>
                <span className={styles.fpQLabel}>
                  What would you like help with today?
                  <span className={styles.fpQSub}> — Select one</span>
                </span>
              </div>
              <div className={styles.fpChipGrid}>
                {concernsList.map(c => (
                  <button
                    key={c.value}
                    className={`${styles.fpChip}${concern === c.value ? ` ${styles.fpChipSel}` : ""}`}
                    onClick={() => handleConcernSelect(c.value)}
                  >
                    <span style={{ fontSize: 16 }}>{c.icon}</span>
                    <span style={{ fontSize: "clamp(11px,1.3vw,12px)" }}>{c.label}</span>
                  </button>
                ))}
              </div>

              {/* Symptoms for selected concern */}
              {concern && symptomsList.length > 0 && (
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--ava-color-primary-50)" }}>
                  <div className={styles.fpQHeader}>
                    <div className={`${styles.fpQNum}${symptoms.length > 0 ? ` ${styles.fpQNumDone}` : ""}`}>
                      {symptoms.length > 0 ? "✓" : "→"}
                    </div>
                    <span className={styles.fpQLabel}>
                      Which symptoms are you experiencing?
                      <span className={styles.fpQSub}> — Select all that apply</span>
                    </span>
                  </div>
                  <div className={styles.fpChipGrid}>
                    {symptomsList.map(s => {
                      const sel = symptoms.includes(s.value);
                      return (
                        <button
                          key={s.value}
                          className={`${styles.fpChip}${sel ? ` ${styles.fpChipSel}` : ""}`}
                          onClick={() => toggleSymptom(s.value)}
                          style={{ position: "relative" }}
                        >
                          {sel && <span className={styles.fpCheckDot}>✓</span>}
                          <span style={{ fontSize: 16 }}>{s.icon}</span>
                          <span style={{ fontSize: "clamp(11px,1.3vw,12px)" }}>{s.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 2: Safety + Duration ── */}
          {step === 2 && (
            <div className={styles.fpQSection}>
              <div className={styles.fpQHeader}>
                <div className={`${styles.fpQNum}${safety.length > 0 ? ` ${styles.fpQNumDone}` : ""}`}>
                  {safety.length > 0 ? "✓" : 3}
                </div>
                <span className={styles.fpQLabel}>Are you experiencing any of these today?</span>
              </div>
              <div className={styles.fpChipGrid}>
                {["⚡ Severe Pain", "🩸 Heavy Bleeding", "💫 Dizziness", "🌡 Fever", "✅ None of These"].map(s => {
                  const parts = s.split(" "), icon = parts[0], label = parts.slice(1).join(" ");
                  const isNone = label === "None of These", val = isNone ? "None of the above" : label;
                  const sel = safety.includes(val);
                  return (
                    <button
                      key={s}
                      className={`${styles.fpChip}${sel ? (isNone ? ` ${styles.fpChipSelGreen}` : ` ${styles.fpChipSelRed}`) : ""}`}
                      onClick={() => setSafety(isNone ? ["None of the above"] : [val])}
                    >
                      <span style={{ fontSize: 16 }}>{icon}</span>
                      <span style={{ fontSize: "clamp(10px,1.3vw,12px)" }}>{label}</span>
                    </button>
                  );
                })}
              </div>
              <p style={{ fontSize: 11, color: "var(--ava-color-caption)", marginTop: 8 }}>
                If you're experiencing severe symptoms, please contact a healthcare professional.
              </p>

              {/* Duration merged into step 2 */}
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--ava-color-primary-50)" }}>
                <div className={styles.fpQHeader}>
                  <div className={`${styles.fpQNum}${duration ? ` ${styles.fpQNumDone}` : ""}`}>
                    {duration ? "✓" : 4}
                  </div>
                  <span className={styles.fpQLabel}>How long has this been affecting you?</span>
                </div>
                <div className={styles.fpChipGrid}>
                  {["A few days", "1–4 weeks", "1–3 months", "3+ months"].map(d => (
                    <button
                      key={d}
                      className={`${styles.fpChip}${duration === d ? ` ${styles.fpChipSel}` : ""}`}
                      onClick={() => setDuration(d)}
                    >
                      <span style={{ fontSize: "clamp(11px,1.3vw,12px)" }}>{d}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 3: Next Step ── */}
          {step === 3 && (
            <div className={styles.fpQSection}>
              <div className={styles.fpQHeader}>
                <div className={`${styles.fpQNum}${nextStep ? ` ${styles.fpQNumDone}` : ""}`}>
                  {nextStep ? "✓" : 5}
                </div>
                <span className={styles.fpQLabel}>What would you prefer next?</span>
              </div>
              <div className={styles.fpChipGrid}>
                {[
                  { label: "Book Doctor", icon: "📅" },
                  { label: "Take Assessment", icon: "📋" },
                  { label: "Explore Program", icon: "🔍" },
                  { label: "Request Callback", icon: "📞" },
                ].map(n => (
                  <button
                    key={n.label}
                    className={`${styles.fpChip}${nextStep === n.label ? ` ${styles.fpChipSel}` : ""}`}
                    onClick={() => setNextStep(n.label)}
                  >
                    <span style={{ fontSize: 20 }}>{n.icon}</span>
                    <span style={{ fontSize: 12 }}>{n.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Footer nav */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            paddingTop: 16, borderTop: "1px solid var(--ava-color-primary-50)",
            flexWrap: "wrap", gap: 8
          }}>
            <Btn variant="ghost" onClick={() => step === 0 ? setScreen("landing") : setStep(s => s - 1)}>
              ← Back
            </Btn>
            {step < STEPS.length - 1 ? (
              <Btn disabled={!stepValid[step]} onClick={() => setStep(s => s + 1)}>
                Next →
              </Btn>
            ) : (
              <Btn disabled={!stepValid[step]} onClick={handleSeePrograms}>
                Continue →
              </Btn>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div>
          <SummaryCard data={answerData} />
          <CareNavigatorCard />
        </div>
      </div>
      <TrustBar />
    </div>
  );
};
// ─────────────────────────────────────────────────────────────
// SCREEN: ELIGIBILITY  (dynamic questions from selected program)
// ─────────────────────────────────────────────────────────────
const EligibilityScreen = ({ setScreen, selectedProgram, answers, setAnswers }) => {
  const prog = selectedProgram || DATA.programs[0];

  const handleAnswer = (id, val) => setAnswers(prev => ({ ...prev, [id]: val }));

  const answeredCount = prog.eligibilityQuestions.filter(q => {
    const v = answers[q.id];
    return q.type === "multi" ? (v || []).length > 0 : !!v;
  }).length;
  const allAnswered = answeredCount === prog.eligibilityQuestions.length;

  return (
    <div className={`${styles.fpContainer} ${styles.fpScreen}`}>
      <h1 style={{ fontSize: "clamp(18px,3vw,28px)", fontWeight: 900, marginBottom: 4 }}>Check your eligibility</h1>
      <p style={{ fontSize: 13, color: "var(--ava-color-body)", marginBottom: 24 }}>
        Answer a few questions to see if this program is the right fit for your needs.
      </p>
      <div className={styles.fpTwoCol}>
        <div className={styles.fpCard}>
          <Stepper steps={["Program", "Eligibility Questions", "Safety Check", "Result"]} current={1} />

          {/* Program banner */}
          <div className={styles.fpProgHeader}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: "linear-gradient(135deg, var(--ava-color-secondary-50), var(--ava-color-primary-100))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>
              {prog.emoji}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{prog.name}</div>
              <div style={{ fontSize: 12, color: "var(--ava-color-caption)" }}>{prog.short}</div>
            </div>
            <Badge variant="blue">⏳ In progress</Badge>
          </div>

          {/* Dynamic questions — rendered from prog.eligibilityQuestions */}
          {prog.eligibilityQuestions.map(q => (
            <QSection key={q.id} q={q} answers={answers} onAnswer={handleAnswer} />
          ))}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 16, borderTop: "1px solid var(--ava-color-primary-50)", flexWrap: "wrap", gap: 8 }}>
            <Btn variant="ghost" onClick={() => setScreen("listing")}>← Back</Btn>
            <Btn lg onClick={() => setScreen("safety")} disabled={!allAnswered}>
              Continue to Safety Check →
            </Btn>
          </div>
          {!allAnswered && (
            <p style={{ fontSize: 11, color: "var(--ava-color-caption)", marginTop: 8, textAlign: "right" }}>
              Please answer all {prog.eligibilityQuestions.length} questions to continue
            </p>
          )}
        </div>

        <div>
          <SummaryCard data={{ lifeStage: prog.tags[0] || "—", concern: prog.name, safetyStatus: "—", duration: "—", nextStep: "—" }} />
          <div className={styles.fpCard} style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>How eligibility works</div>
            {[
              { icon: "👥", title: "Matched to your life stage", desc: "We consider your life stage to ensure age-appropriate care." },
              { icon: "🛡️", title: "Evaluated against symptoms", desc: "Your answers are matched with program clinical criteria." },
              { icon: "💜", title: "Safety reviewed first", desc: "We check for safety and suitability before recommending next steps." },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: i < 2 ? 12 : 0 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--ava-color-primary-100)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{item.icon}</div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 2 }}>{item.title}</div>
                  <div style={{ fontSize: 10, color: "var(--ava-color-caption)", lineHeight: 1.4 }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <CareNavigatorCard />
        </div>
      </div>
      <TrustBar />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SCREEN: SAFETY CHECK
// ─────────────────────────────────────────────────────────────
const SafetyCheckScreen = ({ setScreen, selectedProgram, selected, setSelected, worsening, setWorsening, activities, setActivities, urgentReports, setUrgentReports }) => {
  const prog = selectedProgram || DATA.programs[0];

  const toggleSym = (label) => {
    if (label === "None of the above") { setSelected(["None of the above"]); return; }
    setSelected(prev => {
      const rest = prev.filter(x => x !== "None of the above");
      return rest.includes(label) ? rest.filter(x => x !== label) : [...rest, label];
    });
  };
  const isRoutine = selected.includes("None of the above") || selected.length === 0;

  return (
    <div className={`${styles.fpContainer} ${styles.fpScreen}`}>
      <h1 style={{ fontSize: "clamp(18px,3vw,28px)", fontWeight: 900, marginBottom: 4 }}>Safety Check</h1>
      <p style={{ fontSize: 13, color: "var(--ava-color-body)", marginBottom: 24 }}>
        Before we recommend a program, let's quickly check if you need urgent care today.
      </p>
      <div className={styles.fpTwoCol}>
        <div className={styles.fpCard}>
          <Stepper steps={["Program", "Eligibility Questions", "Safety Check", "Result"]} current={2} />
          <div className={styles.fpProgHeader}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: "linear-gradient(135deg, var(--ava-color-secondary-50), var(--ava-color-primary-100))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>
              {prog.emoji}
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{prog.name}</div>
              <div style={{ fontSize: 12, color: "var(--ava-color-caption)" }}>{prog.short}</div>
            </div>
          </div>

          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>
            Are you experiencing any of these symptoms today?
          </div>
          <div className={styles.fpSymGrid}>
            {DATA.safetySymptoms.map((s, i) => {
              const sel = selected.includes(s.label);
              return (
                <div
                  key={i}
                  onClick={() => toggleSym(s.label)}
                  className={`${styles.fpSymRow}${s.urgent && sel ? ` ${styles.fpSymRowUrgentSel}` : sel ? ` ${styles.fpSymRowSafeSel}` : ""}`}
                >
                  <span style={{ fontSize: 22 }}>{s.emoji}</span>
                  <div style={{ flex: 1, fontSize: 12, fontWeight: sel ? 600 : 400, lineHeight: 1.3, color: s.urgent && sel ? "var(--ava-color-error-500)" : sel ? "var(--ava-color-success-500)" : "var(--ava-color-heading)" }}>
                    {s.label}
                  </div>
                  <div className={styles.fpSymCheck} style={{ border: `2px solid ${s.urgent && sel ? "var(--ava-color-error-500)" : sel ? "var(--ava-color-success-500)" : "var(--ava-border-color)"}`, background: sel ? (s.urgent ? "var(--ava-color-error-500)" : "var(--ava-color-success-500)") : "var(--ava-color-white)" }}>
                    {sel && <span style={{ color: "#fff", fontSize: 10 }}>✓</span>}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ borderTop: "1px solid var(--ava-color-primary-50)", paddingTop: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Additional checks</div>
            {[
              { label: "Are your symptoms getting worse quickly?", val: worsening, set: setWorsening },
              { label: "Are you unable to continue normal daily activities?", val: activities, set: setActivities },
              { label: "Do you have any urgent reports to review today?", val: urgentReports, set: setUrgentReports },
            ].map((q, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, padding: "12px 0", borderBottom: i < 2 ? "1px solid var(--ava-color-primary-50)" : "none", flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontSize: 13, color: "var(--ava-color-body)", flex: 1 }}>{q.label}</span>
                <div style={{ display: "flex", gap: 8 }}>
                  {["Yes", "No"].map(opt => (
                    <button key={opt} onClick={() => q.set(opt)} style={{ padding: "6px 16px", border: `1.5px solid ${q.val === opt ? "var(--ava-color-primary-700)" : "var(--ava-border-color)"}`, borderRadius: 6, background: q.val === opt ? "var(--ava-color-primary-100)" : "var(--ava-color-white)", cursor: "pointer", fontSize: 12, fontWeight: q.val === opt ? 700 : 400, color: q.val === opt ? "var(--ava-color-primary-700)" : "var(--ava-color-caption)", fontFamily: "var(--ava-font-family)" }}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.fpInfoBox} style={{ marginTop: 12 }}>
            ℹ This quick safety check helps us guide you to the right next step. It is not a diagnosis.
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20, flexWrap: "wrap", gap: 8 }}>
            <Btn variant="ghost" onClick={() => setScreen("eligibility")}>← Back</Btn>
            <div style={{ display: "flex", gap: 12 }}>
              {/* <Btn variant="secondary">💬 Ask Care Navigator</Btn> */}
              <Btn lg onClick={() => setScreen("result")}>Continue →</Btn>
            </div>
          </div>
        </div>

        <div>
          {/* <div className={styles.fpCard}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>Your Care Summary</div>
            {[["Program", prog.name], ["Tags", prog.tags[0] || "—"], ["Type", prog.type], ["Duration", prog.duration]].map(([l, v], i) => (
              <div key={i} className={styles.fpSumRow}>
                <span className={styles.fpSumLabel}>{l}</span>
                <span className={styles.fpSumVal}>{v}</span>
              </div>
            ))}
          </div>
          <div className={styles.fpCard} style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Safety Status</div>
            <div className={`${styles.fpSafetyBox} ${isRoutine ? styles.fpSafetyBoxRoutine : styles.fpSafetyBoxUrgent}`}>
              <div className={styles.fpSafetyIcon}>{isRoutine ? "🛡️" : "⚠️"}</div>
              <div>
                <Badge variant={isRoutine ? "green" : "orange"}>{isRoutine ? "Routine" : "Urgent"}</Badge>
                <div style={{ fontSize: 11, color: "var(--ava-color-caption)", marginTop: 4, lineHeight: 1.4 }}>
                  {isRoutine ? "No urgent concerns identified." : "Some symptoms may need immediate attention."}
                </div>
              </div>
            </div>
          </div> */}
            <SummaryCard data={{ 
    lifeStage: prog.tags[0] || "—", 
    concern: prog.name, 
    safetyStatus: isRoutine ? "Routine" : "Urgent", 
    duration: prog.duration, 
    nextStep: "—" 
  }} />
          <CareNavigatorCard />
        </div>
      </div>
      <TrustBar />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SCREEN: ELIGIBILITY RESULT
// ─────────────────────────────────────────────────────────────
const EligibilityResultScreen = ({ setScreen, selectedProgram }) => {
  const prog = selectedProgram || DATA.programs[0];
  return (
    <div className={`${styles.fpContainer} ${styles.fpScreen}`}>
      <h1 style={{ fontSize: "clamp(18px,3vw,28px)", fontWeight: 900, marginBottom: 4 }}>Your eligibility result</h1>
      <p style={{ fontSize: 13, color: "var(--ava-color-body)", marginBottom: 24 }}>
        Based on your answers, this program is a strong fit for your current health needs.
      </p>
      <div className={styles.fpTwoCol}>
        <div>
          <Stepper steps={["Program", "Eligibility Questions", "Safety Check", "Result"]} current={3} />
          <div className={styles.fpCard}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
              <div style={{ width: 64, height: 64, borderRadius: 16, background: "linear-gradient(135deg, var(--ava-color-secondary-50), var(--ava-color-primary-100))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, flexShrink: 0 }}>
                {prog.emoji}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "clamp(16px,2vw,20px)", fontWeight: 800, marginBottom: 6 }}>{prog.name}</div>
                <div style={{ fontSize: 13, color: "var(--ava-color-caption)" }}>{prog.short}</div>
              </div>
              <div className={styles.fpEligibleBox}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <span>✅</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: "var(--ava-color-success-500)" }}>Eligible</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--ava-color-success-500)" }}>You are eligible for this program.</div>
              </div>
            </div>

            <div className={styles.fpScoreBox}>
              <span style={{ fontSize: 24 }}>⭐</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: "var(--ava-color-caption)", marginBottom: 4 }}>Eligibility Score</div>
                <div style={{ fontSize: 28, fontWeight: 900, color: "var(--ava-color-primary-700)" }}>
                  85<span style={{ fontSize: 16, color: "var(--ava-color-caption)", fontWeight: 400 }}>/100</span>
                </div>
              </div>
              <div style={{ flex: 2, minWidth: 200 }}>
                <div className={styles.fpScoreTrack}><div className={styles.fpScoreFill} style={{ width: "85%" }} /></div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--ava-color-caption)", marginTop: 4 }}>
                  <span>0</span><span>100</span>
                </div>
              </div>
            </div>

            <div className={styles.fpSnapGrid} style={{ marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Why this is recommended</div>
                {["Life stage matched to program criteria", "Primary concern aligns with program focus", "Reported symptoms match program scope", "Moderate risk identified — early intervention ideal", "No urgent safety concerns detected"].map((r, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>✅</span>
                    <span style={{ fontSize: 12, color: "var(--ava-color-body)", lineHeight: 1.4 }}>{r}</span>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>What's included</div>
                {prog.includes.map((inc, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <div className={styles.fpIncludeIcon}>{inc.icon}</div>
                    <span style={{ fontSize: 12, color: "var(--ava-color-body)" }}>{inc.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Btn onClick={() => setScreen("enrollment")}>👥 Join Program</Btn>
              <Btn variant="secondary" onClick={() => setScreen("doctor")}>📅 Book Doctor First</Btn>
              <Btn variant="ghost">📋 Take Assessment</Btn>
              <Btn variant="ghost">☁️ Upload Reports</Btn>
            </div> */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, flexWrap: "wrap" }}>
              <Btn onClick={() => setScreen("enrollment")}>👥 Join Program</Btn>
              <Btn variant="ghost">📋 Take Assessment</Btn>
            </div>
          </div>
        </div>

        <div>
          <SummaryCard data={{ lifeStage: prog.tags[0], concern: prog.name, safetyStatus: "Routine", duration: prog.duration, nextStep: "Book Doctor First" }} />
          <div className={styles.fpCard} style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Next best step</div>
            <div style={{ fontSize: 12, color: "var(--ava-color-caption)", marginBottom: 14, lineHeight: 1.4 }}>
              A consultation is recommended to begin your personalised care plan.
            </div>
            <div style={{ border: "1px solid var(--ava-border-color)", borderRadius: "var(--ava-radius-md)", padding: 14 }}>
  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
    <div className={styles.fpDoctorAvatar}>👩</div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 700 }}>{DATA.doctors[0].name}</div>
      <div style={{ fontSize: 11, color: "var(--ava-color-caption)" }}>{DATA.doctors[0].role} · {DATA.doctors[0].exp}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span className={styles.fpOnlineDot} />
        <span style={{ fontSize: 10, color: "var(--ava-color-success-500)" }}>Online</span>
      </div>
    </div>
  </div>
  <Btn variant="secondary" full>📅 View Slots</Btn>
</div>
          </div>
          <CareNavigatorCard />
        </div>
      </div>
      <TrustBar />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SCREEN: PROGRAM DETAIL
// ─────────────────────────────────────────────────────────────
const ProgramDetailScreen = ({ setScreen, selectedProgram }) => {
  const prog = selectedProgram || DATA.programs[0];
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className={`${styles.fpContainer} ${styles.fpScreen}`}>
      <span style={{ fontSize: 13, color: "var(--ava-color-primary-700)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, marginBottom: 16, fontWeight: 600 }} onClick={() => setScreen("listing")}>
        ← Back to Programs
      </span>
      <div className={styles.fpTwoCol}>
        <div>
          <div className={styles.fpCard} style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 20, flexWrap: "wrap" }}>
              <div style={{ width: 80, height: 80, borderRadius: 20, background: "linear-gradient(135deg, var(--ava-color-secondary-50), var(--ava-color-primary-100))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40, flexShrink: 0 }}>{prog.emoji}</div>
              <div style={{ flex: 1 }}>
                <h1 style={{ fontSize: "clamp(18px,3vw,24px)", fontWeight: 900, marginBottom: 6 }}>{prog.name}</h1>
                <div style={{ fontSize: 13, color: "var(--ava-color-caption)", marginBottom: 10 }}>{prog.short}</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {prog.tags.map(t => <Badge key={t} variant="purple">{t}</Badge>)}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <Btn onClick={() => setScreen("enrollment")}>👥 Join Program</Btn>
                <Btn variant="secondary" onClick={() => setScreen("doctor")}>📅 Book Doctor First</Btn>
              </div>
            </div>
            <p style={{ fontSize: 13, color: "var(--ava-color-body)", lineHeight: 1.6, marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--ava-color-primary-50)" }}>{prog.description}</p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16, marginBottom: 16 }}>
            <div className={styles.fpCard}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>👥 Who this program is for</div>
              {prog.whoFor.map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                  <span style={{ color: "var(--ava-color-secondary-700)", fontSize: 14, flexShrink: 0, marginTop: 1 }}>✓</span>
                  <span style={{ fontSize: 12, color: "var(--ava-color-body)", lineHeight: 1.4 }}>{r}</span>
                </div>
              ))}
            </div>
            <div className={styles.fpCard}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>📋 What's included</div>
              <div className={styles.fpIncGrid}>
                {prog.includes.map((inc, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div className={styles.fpIncludeIcon}>{inc.icon}</div>
                    <span style={{ fontSize: 11, color: "var(--ava-color-body)", fontWeight: 500 }}>{inc.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.fpCard} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Frequently asked questions</div>
            {prog.faqs.map((faq, i) => (
              <div key={i} style={{ borderBottom: i < prog.faqs.length - 1 ? "1px solid var(--ava-color-primary-50)" : "none" }}>
                <button className={styles.fpFaqBtn} onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span style={{ fontSize: 13, color: "var(--ava-color-heading)", fontWeight: 500, textAlign: "left" }}>{faq.q}</span>
                  <span style={{ fontSize: 16, color: "var(--ava-color-caption)", flexShrink: 0 }}>{openFaq === i ? "▲" : "▼"}</span>
                </button>
                {openFaq === i && <div className={styles.fpFaqAns}>{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className={styles.fpCard} style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Program Snapshot</div>
              <Badge variant="green">✅ Eligible</Badge>
            </div>
            {[["⏱️", "Duration", prog.duration], ["🖥️", "Care Mode", prog.mode], ["👥", "Care Team", "Specialist + Coordinator"], ["🔄", "Follow-up", prog.followUp]].map(([icon, label, val], i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 0", borderBottom: i < 3 ? "1px solid var(--ava-color-primary-50)" : "none" }}>
                <span style={{ fontSize: 16 }}>{icon}</span>
                <div>
                  <div style={{ fontSize: 11, color: "var(--ava-color-caption)", marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{val}</div>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.fpCard}>
            <Btn full onClick={() => setScreen("enrollment")}>👥 Join Program</Btn>
            <Btn variant="secondary" full onClick={() => setScreen("doctor")}>📅 Book Doctor First</Btn>
            {/* <Btn variant="ghost" full>☁️ Upload Reports</Btn> */}
            <div style={{ paddingTop: 14, borderTop: "1px solid var(--ava-color-primary-50)", fontSize: 11, color: "var(--ava-color-caption)", marginBottom: 10 }}>
              Need help? Our care navigator can guide you.
            </div>
            <Btn variant="ghost" full>💬 Chat with Care Navigator</Btn>
          </div>
        </div>
      </div>
      <TrustBar />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SCREEN: DOCTOR REVIEW
// ─────────────────────────────────────────────────────────────
const DoctorReviewScreen = ({ setScreen, selectedProgram }) => {
  const prog = selectedProgram || DATA.programs[0];
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");

  return (
    <div className={`${styles.fpContainer} ${styles.fpScreen}`}>
      <span style={{ fontSize: 13, color: "var(--ava-color-primary-700)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, marginBottom: 16, fontWeight: 600 }} onClick={() => setScreen("listing")}>
        ← Back to Programs
      </span>
      <div className={styles.fpTwoCol}>
        <div>
          <div className={styles.fpCard} style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
              <div style={{ width: 60, height: 60, borderRadius: 16, background: "var(--ava-color-error-100)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, flexShrink: 0 }}>🩺</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
                  <h1 style={{ fontSize: "clamp(16px,2.5vw,22px)", fontWeight: 900 }}>Doctor Review Required</h1>
                  <Badge variant="orange">🔄 Doctor Review Required</Badge>
                </div>
                <p style={{ fontSize: 13, color: "var(--ava-color-caption)" }}>
                  A specialist review is recommended before you join the {prog.name}.
                </p>
              </div>
            </div>
            <div className={styles.fpInfoBox}>ℹ This review helps personalize your care plan and ensures the right support for you.</div>
          </div>

          <div className={styles.fpCard}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Recommended doctors</div>
            <div className={styles.fpDocGrid}>
              {DATA.doctors.map((doc, i) => (
                <div key={i} className={styles.fpDocCard}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <div className={styles.fpDoctorAvatar}>👩</div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>{doc.name}</div>
                      <div style={{ fontSize: 11, color: "var(--ava-color-caption)" }}>{doc.role} · {doc.exp}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
                    {doc.tags.map(t => <Badge key={t} variant="purple">{t}</Badge>)}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--ava-color-caption)", marginBottom: 8 }}>📅 Next available: Today, 10:30 AM</div>
                  <Btn full>Book This Doctor</Btn>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--ava-color-primary-50)" }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Available time slots</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {DATA.days.map(d => (
                  <button key={d} className={`${styles.fpDayBtn}${selectedDate === d ? ` ${styles.fpDayBtnSel}` : ""}`} onClick={() => setSelectedDate(d)}>
                    {d}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {DATA.times.map(t => (
                  <button key={t} className={`${styles.fpSlotBtn}${selectedTime === t ? ` ${styles.fpSlotBtnSel}` : ""}`} onClick={() => setSelectedTime(t)}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <SummaryCard data={{ lifeStage: prog.tags[0], concern: prog.name, safetyStatus: "Routine", duration: prog.duration, nextStep: "Book Doctor First" }} />
          <div className={styles.fpCard} style={{ marginTop: 16 }}>
            <Btn full>📅 Book Doctor First</Btn>
            {/* <Btn variant="secondary" full>☁️ Upload Reports</Btn> */}
            <Btn variant="ghost" full>💬 Chat with Care Navigator</Btn>
          </div>
        </div>
      </div>
      <TrustBar />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SCREEN: ENROLLMENT CONFIRMATION
// ─────────────────────────────────────────────────────────────
const EnrollmentConfirmationScreen = ({ setScreen, selectedProgram }) => {
  const prog = selectedProgram || DATA.programs[0];
  return (
    <div className={`${styles.fpContainer} ${styles.fpScreen}`}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
        <div style={{ width: 56, height: 56, borderRadius: "50%", background: "var(--ava-color-success-100)", border: "2px solid var(--ava-color-success-200)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, flexShrink: 0 }}>✅</div>
        <div>
          <h1 style={{ fontSize: "clamp(18px,3vw,26px)", fontWeight: 900, marginBottom: 4 }}>Enrollment Confirmation</h1>
          <p style={{ fontSize: 13, color: "var(--ava-color-body)" }}>
            You're successfully enrolled in the {prog.name}. Your personalised care journey is now active.
          </p>
        </div>
      </div>

      <div className={styles.fpTwoCol}>
        <div>
          <div className={styles.fpCard} style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: "linear-gradient(135deg, var(--ava-color-secondary-50), var(--ava-color-primary-100))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>{prog.emoji}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ fontSize: "clamp(14px,2vw,18px)", fontWeight: 800 }}>{prog.name}</div>
                  <Badge variant="green">✅ Enrolled</Badge>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {[["📋", "Enrollment ID", "ENR-20481"], ["📅", "Start Date", "19 May 2026"], ["⏱️", "Duration", prog.duration]].map(([emoji, label, val]) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 16 }}>{emoji}</span>
                  <div>
                    <div style={{ fontSize: 10, color: "var(--ava-color-caption)" }}>{label}</div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{val}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.fpCard} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>What happens next</div>
            <div className={styles.fpNextGrid}>
              {[
                { icon: "📋", title: "Complete onboarding questionnaire", badge: "green", badgeLabel: "Ready", cta: "Start Now →" },
                { icon: "📅", title: "Book your first consultation", badge: "orange", badgeLabel: "Pending", cta: "Book Now →" },
                { icon: "☁️", title: "Upload previous reports", badge: "blue", badgeLabel: "Optional", cta: "Upload Now →" },
                { icon: "📊", title: "Start tracking", badge: "green", badgeLabel: "Ready", cta: "Start Tracking →" },
              ].map((s, i) => (
                <div key={i} className={styles.fpNextCard}>
                  <div className={styles.fpNextCardIcon}>{s.icon}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, lineHeight: 1.3 }}>{s.title}</div>
                  <Badge variant={s.badge}>{s.badgeLabel}</Badge>
                  <div style={{ fontSize: 11, color: "var(--ava-color-primary-700)", fontWeight: 600, marginTop: 8, cursor: "pointer" }}>{s.cta}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className={styles.fpCard}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>Enrollment Summary</div>
            {[["Program", prog.name, false], ["Type", prog.type, false], ["Mode", prog.mode, false], ["Safety Status", "Routine", true]].map(([l, v, green], i) => (
              <div key={i} className={styles.fpSumRow}>
                <span className={styles.fpSumLabel}>{l}</span>
                <span className={`${styles.fpSumVal}${green ? ` ${styles.fpSumValGreen}` : ""}`}>{v}</span>
              </div>
            ))}
          </div>
          <div className={styles.fpCard} style={{ marginTop: 16 }}>
            <div className={styles.fpInfoBox} style={{ marginBottom: 14 }}>
              💡 Book your first consultation and complete onboarding questions.
            </div>
            <Btn full>👥 Go to My Care</Btn>
            <Btn variant="secondary" full>📅 Book First Consultation</Btn>
            {/* <Btn variant="ghost" full>☁️ Upload Reports</Btn> */}
          </div>
          <CareNavigatorCard />
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ROOT APP
// ─────────────────────────────────────────────────────────────
export default function CareProgramApp() {
  const [screen, setScreen] = useState("landing");
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedLifeStage, setSelectedLifeStage] = useState(null);

  const [eligibilityAnswers, setEligibilityAnswers] = useState({});
const [safetySelected, setSafetySelected] = useState([]);
const [worsening, setWorsening] = useState("");
const [activities, setActivities] = useState("");
const [urgentReports, setUrgentReports] = useState("");

  const goTo = useCallback((s) => {
    setScreen(s);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const screens = {
    landing: <LandingScreen setScreen={goTo} setSelectedLifeStage={setSelectedLifeStage} />,
    listing: <ListingScreen setScreen={goTo} setSelectedProgram={setSelectedProgram} selectedLifeStage={selectedLifeStage} setSelectedLifeStage={setSelectedLifeStage} />,
    // choose:      <HelpMeChooseScreen setScreen={goTo} />,

    choose: <HelpMeChooseScreen setScreen={goTo} setSelectedLifeStage={setSelectedLifeStage} />,
    // eligibility: <EligibilityScreen setScreen={goTo} selectedProgram={selectedProgram} />,
eligibility: <EligibilityScreen 
  setScreen={goTo} 
  selectedProgram={selectedProgram}
  answers={eligibilityAnswers}
  setAnswers={setEligibilityAnswers}
/>,
safety: <SafetyCheckScreen 
  setScreen={goTo} 
  selectedProgram={selectedProgram}
  selected={safetySelected}
  setSelected={setSafetySelected}
  worsening={worsening}
  setWorsening={setWorsening}
  activities={activities}
  setActivities={setActivities}
  urgentReports={urgentReports}
  setUrgentReports={setUrgentReports}
/>,
    // safety: <SafetyCheckScreen setScreen={goTo} selectedProgram={selectedProgram} />,
    result: <EligibilityResultScreen setScreen={goTo} selectedProgram={selectedProgram} />,
    doctor: <DoctorReviewScreen setScreen={goTo} selectedProgram={selectedProgram} />,
    detail: <ProgramDetailScreen setScreen={goTo} selectedProgram={selectedProgram} />,
    enrollment: <EnrollmentConfirmationScreen setScreen={goTo} selectedProgram={selectedProgram} />,
    programNotAvailable: (
      <ProgramNotAvailable
        onBack={() => goTo("listing")}
        onViewOtherPrograms={() => goTo("listing")}
        onBookConsultation={() => goTo("doctor")}
      />
    ),

  };

  return (
    <div> 
      <DigitalHealthFAB/>
    {/* <div className={styles.page}> */}
    {/* <div> */}
      {screens[screen] || screens.landing}
      {/* <BottomNav screen={screen} setScreen={goTo} /> */}
    {/* </div> */}
    </div>
  );
}